import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

interface IndustriesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onBookDemo: () => void;
}

const industries = [
  {
    icon: 'ri-plant-line',
    title: 'Outdoor & Landscape Services',
    description: 'Lawn care, landscaping, tree trimming, and irrigation companies thrive with automated scheduling, seasonal reminders, and route-optimized booking.',
    useCases: ['Recurring lawn maintenance scheduling', 'Seasonal service reminders', 'Crew dispatch & job routing', 'Estimate request automation'],
    color: 'from-green-500 to-teal-500',
    link: '/ai-for-landscapers',
  },
  {
    icon: 'ri-tools-line',
    title: 'Appliance Repair',
    description: 'Appliance repair techs can stop playing phone tag. Let customers book service windows online while your AI handles after-hours calls.',
    useCases: ['Service window booking', '24/7 AI call answering', 'Technician dispatch scheduling', 'Follow-up & review requests'],
    color: 'from-orange-500 to-amber-500',
    link: '/industries/ai-for-appliance-repair',
  },
  {
    icon: 'ri-door-open-line',
    title: 'Garage Door Repair',
    description: 'Emergency and routine garage door services need fast response. Voice AI captures urgent calls instantly and books same-day appointments.',
    useCases: ['Emergency call capture 24/7', 'Same-day appointment booking', 'Maintenance reminder campaigns', 'Customer follow-up automation'],
    color: 'from-slate-500 to-gray-600',
  },
  {
    icon: 'ri-hammer-line',
    title: 'Carpenters & Contractors',
    description: 'Custom woodworkers and general contractors can showcase their work, collect project inquiries, and schedule consultations automatically.',
    useCases: ['Project consultation booking', 'Portfolio website with inquiry forms', 'Estimate follow-up sequences', 'Client communication automation'],
    color: 'from-amber-600 to-yellow-500',
    link: '/ai-for-contractors',
  },
  {
    icon: 'ri-home-3-line',
    title: 'Roofing Companies',
    description: 'Roofing contractors can capture storm-damage leads around the clock, automate estimate requests, and follow up with homeowners who never called back.',
    useCases: ['Storm damage lead capture 24/7', 'Estimate request & scheduling', 'Insurance claim follow-up automation', 'Review generation after installs'],
    color: 'from-stone-500 to-slate-600',
    link: '/ai-for-roofers',
  },
  {
    icon: 'ri-brush-line',
    title: 'Painters',
    description: 'Interior and exterior painters can automate quote requests, schedule walkthroughs, and follow up with leads who never called back.',
    useCases: ['Quote request & walkthrough booking', 'Lead follow-up automation', 'Seasonal campaign outreach', 'Review generation after jobs'],
    color: 'from-rose-500 to-pink-500',
    link: '/industries/ai-for-painters',
  },
  {
    icon: 'ri-heart-pulse-line',
    title: 'Fitness Centers & Gyms',
    description: 'Gyms, personal trainers, and yoga studios can automate class bookings, membership inquiries, and trial session scheduling.',
    useCases: ['Class & session booking', 'Trial membership scheduling', 'Membership renewal reminders', 'Group class waitlist management'],
    color: 'from-primary to-teal-400',
  },
  {
    icon: 'ri-gamepad-line',
    title: 'Bar Arcades & Entertainment',
    description: 'Barcades, escape rooms, and entertainment venues can manage event reservations, private party bookings, and table reservations effortlessly.',
    useCases: ['Private event & party booking', 'Table & lane reservations', 'Birthday package inquiries', 'Event reminder & confirmation texts'],
    color: 'from-violet-500 to-fuchsia-500',
  },
  {
    icon: 'ri-caravan-line',
    title: 'RV Parks & Campgrounds',
    description: 'RV parks and campgrounds can automate site reservations, check-in reminders, and seasonal availability updates for guests.',
    useCases: ['Site & cabin reservations', 'Check-in/check-out reminders', 'Seasonal availability campaigns', 'Guest review requests'],
    color: 'from-emerald-500 to-green-600',
    link: '/ai-for-rv-parks',
  },
  {
    icon: 'ri-scissors-line',
    title: 'Salons & Spas',
    description: 'Hair salons, nail studios, and day spas can fill their books automatically with online booking, reminders, and rebooking campaigns.',
    useCases: ['Online appointment booking', 'No-show reduction reminders', 'Rebooking & loyalty campaigns', 'New service announcements'],
    color: 'from-pink-400 to-rose-400',
    link: '/industries/ai-for-salons-spas',
  },
  {
    icon: 'ri-stethoscope-line',
    title: 'Medical & Wellness Clinics',
    description: 'Chiropractors, physical therapists, and wellness clinics can automate new patient intake, appointment reminders, and follow-up care.',
    useCases: ['New patient appointment booking', 'Intake form automation', 'Appointment reminder sequences', 'Follow-up care scheduling'],
    color: 'from-cyan-500 to-sky-500',
  },
  {
    icon: 'ri-car-line',
    title: 'Auto Detailing & Repair',
    description: 'Auto shops and detailers can capture leads from calls and web, book drop-off appointments, and send pickup notifications automatically.',
    useCases: ['Drop-off appointment scheduling', 'Service status notifications', 'Maintenance reminder campaigns', 'After-service review requests'],
    color: 'from-blue-500 to-indigo-500',
  },
  {
    icon: 'ri-home-gear-line',
    title: 'Home Services (HVAC, Plumbing, Electric)',
    description: 'Any home service business benefits from 24/7 call capture, emergency booking, and automated follow-ups that keep the pipeline full.',
    useCases: ['Emergency call answering 24/7', 'Seasonal tune-up campaigns', 'Service agreement renewals', 'Technician scheduling & dispatch'],
    color: 'from-teal-500 to-secondary',
    link: '/industries/ai-for-home-services',
  },
];

export function IndustriesModal({ isOpen, onClose, onBookDemo }: IndustriesModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(onClose, 200);
  };

  const handleBookDemo = () => {
    handleClose();
    setTimeout(onBookDemo, 250);
  };

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-opacity duration-200 ${isAnimating ? 'opacity-100' : 'opacity-0'}`}
      onClick={handleClose}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm"></div>

      <div
        className={`relative bg-white rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto shadow-2xl transition-all duration-200 ${isAnimating ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white z-10 px-8 pt-8 pb-6 border-b border-gray-100">
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl text-gray-600"></i>
          </button>

          <div className="flex items-center gap-4 mb-3">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
              <i className="ri-building-2-line text-2xl text-white"></i>
            </div>
            <div>
              <h3 className="font-display text-3xl font-bold text-secondary">Industries We Serve</h3>
              <p className="text-gray-500 text-sm mt-1">Any business that books appointments or manages a calendar</p>
            </div>
          </div>

          <p className="text-gray-600 leading-relaxed max-w-3xl">
            If your business relies on scheduling, appointments, or calendar-based bookings, Dino Nuggets was built for you. Here are just some of the industries we help automate and grow.
          </p>
        </div>

        {/* Industry Grid */}
        <div className="p-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
            {industries.map((industry, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-xl p-5 hover:shadow-md transition-all duration-200 hover:-translate-y-0.5 flex flex-col"
              >
                <div className={`w-11 h-11 rounded-lg bg-gradient-to-br ${industry.color} flex items-center justify-center mb-4`}>
                  <i className={`${industry.icon} text-xl text-white`}></i>
                </div>
                <h4 className="font-display font-bold text-secondary text-base mb-2">{industry.title}</h4>
                <p className="text-gray-500 text-xs leading-relaxed mb-3">{industry.description}</p>
                <ul className="space-y-1 flex-1">
                  {industry.useCases.map((uc, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <i className="ri-check-line text-primary text-xs mt-0.5 flex-shrink-0"></i>
                      <span className="text-gray-600 text-xs">{uc}</span>
                    </li>
                  ))}
                </ul>
                {industry.link && (
                  <Link
                    to={industry.link}
                    onClick={handleClose}
                    className="mt-4 inline-flex items-center gap-1 text-primary text-xs font-semibold hover:gap-2 transition-all cursor-pointer whitespace-nowrap"
                  >
                    Learn More <i className="ri-arrow-right-line text-sm"></i>
                  </Link>
                )}
              </div>
            ))}
          </div>

          {/* Bottom CTA */}
          <div className="bg-gradient-to-r from-primary to-secondary rounded-2xl p-8 text-white text-center">
            <h4 className="font-display text-2xl font-bold mb-2">Don't see your industry?</h4>
            <p className="text-white/90 text-sm mb-6 max-w-xl mx-auto">
              If you schedule appointments, take bookings, or manage a calendar — we can automate it. Book a free call and we'll show you exactly how it works for your business.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={handleBookDemo}
                className="bg-white text-primary px-8 py-3 rounded-lg font-semibold hover:shadow-lg transition-all cursor-pointer whitespace-nowrap"
              >
                Book a Free Call
              </button>
              <button
                onClick={handleClose}
                className="border-2 border-white/60 text-white px-8 py-3 rounded-lg font-semibold hover:bg-white/10 transition-all cursor-pointer whitespace-nowrap"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
